import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Flame, HeartHandshake } from "lucide-react";
import Logo from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1656343863356-a5efa134a8e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx3b21hbiUyMHN0cmV0Y2hpbmclMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg1OTU2NDAyfDA&ixlib=rb-4.1.0&q=85";
const MEAL_IMG = "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwdmlicmFudCUyMGZvb2QlMjBib3dsfGVufDB8fHx8MTc4NTk1NjQwMnww&ixlib=rb-4.1.0&q=85";
const JOURNAL_IMG = "https://images.unsplash.com/photo-1579017308347-e53e0d2fc5e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx3cml0aW5nJTIwaW4lMjBtZWRpdGF0aW9uJTIwam91cm5hbCUyMHdhcm18ZW58MHx8fHwxNzg1OTU2NDIxfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Pre-launch banner */}
      <div className="bg-[hsl(var(--foreground))] text-[hsl(var(--background))] text-center py-2 px-4 text-xs md:text-sm">
        <span className="uppercase tracking-widest opacity-70 mr-2">Launching December 1, 2026</span>
        Join the waitlist and lock in <strong>$9.99 per month for life</strong> · <Link to="/waitlist" className="underline hover:text-[hsl(var(--primary))]" data-testid="landing-pre-banner">Save my spot →</Link>
      </div>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[hsl(var(--background))]/85 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <Logo size={80} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-stone-600">
            <a href="#pillars" className="hover:text-[hsl(var(--foreground))]">Pillars</a>
            <a href="#weeks" className="hover:text-[hsl(var(--foreground))]">The Months</a>
            <a href="#about" className="hover:text-[hsl(var(--foreground))]">About</a>
            <Link to="/pricing" className="hover:text-[hsl(var(--foreground))]">Membership</Link>
            <Link to="/waitlist" className="text-[hsl(var(--primary))] hover:text-[hsl(var(--foreground))]" data-testid="landing-waitlist-nav">Pre-order</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/auth?mode=login" data-testid="landing-signin" className="text-sm text-stone-600 hover:text-[hsl(var(--foreground))]">Sign in</Link>
            <Link to="/pricing" data-testid="landing-cta-top" className="text-sm bg-[hsl(var(--foreground))] text-[hsl(var(--background))] px-4 py-2 hover:bg-[hsl(var(--primary))] transition-colors">
              Begin
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 fade-in-up">
            <div className="label-eyebrow mb-8">Issue N°01 · A Monthly Practice · <a href="https://satoriom.com" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] not-italic hover:underline" data-testid="hero-satoriom">By Satori OM</a></div>
            <h1 className="font-serif text-[3.5rem] md:text-[5.5rem] lg:text-[7rem] leading-[0.95] tracking-[-0.03em]">
              Come <span className="font-script text-[hsl(var(--primary))] font-normal">home</span>
              <br />
              <em className="italic text-[hsl(var(--primary))] font-normal">to yourself</em>,
              <br />
              1 month at a <span className="font-script text-[hsl(var(--foreground))] font-normal">time</span>.
            </h1>
            <p className="mt-10 max-w-xl text-base leading-relaxed text-stone-600">
              A quiet, monthly practice of nourishing meals, honest movement, and daily
              energetic life coaching — designed for people who are tired of fixing themselves and
              ready to <em className="italic">meet</em> themselves.
            </p>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <Link
                to="/pricing"
                data-testid="landing-cta-hero"
                className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-8 py-4 hover:bg-[hsl(var(--foreground))] transition-colors"
              >
                Begin the practice <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <span className="text-sm text-stone-500">
                <span className="text-[hsl(var(--foreground))] font-medium">$9.99 per month</span> · <Link to="/pricing" className="underline hover:text-[hsl(var(--primary))]" data-testid="hero-pricing-link">See membership</Link>
              </span>
            </div>
            <p className="mt-5 text-sm text-stone-500">
              Already a member? <Link to="/auth?mode=login" className="underline hover:text-[hsl(var(--primary))]" data-testid="hero-signin-link">Sign in</Link>
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="overflow-hidden aspect-[4/5]">
              <img src={HERO} alt="A person stretching in warm light" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[hsl(var(--background))] border border-border p-5 max-w-[260px]">
              <div className="label-eyebrow mb-2">A note from a practitioner</div>
              <p className="font-serif italic text-lg leading-snug">"On the practice everything is in one place, body, mind and spirit."</p>
            </div>
          </div>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* Pillars */}
      <section id="pillars" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="label-eyebrow mb-4">Energy Awakens</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
              Not another <em className="italic">program</em>.
              A <em className="italic text-[hsl(var(--secondary))]">practice</em>.
            </h2>
            <p className="mt-6 text-stone-600 leading-relaxed">
              Three woven threads. One quiet, honest hour a day. Each month unlocking what has always been inside of you.
            </p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-6">
            {[
              { icon: Leaf, title: "Meals", body: "Daily nourishment that fits your busy lifestyle. NO MORE HAVING TO GUESS WHAT'S FOR DINNER!!!!!" },
              { icon: Flame, title: "Movement", body: "From foundation to fire. Mobility, strength, endurance, and dance — in your body's own rhythm." },
              { icon: HeartHandshake, title: "Coaching", body: "A daily coaching session anchored in the current month's theme." },
            ].map((p) => (
              <div key={p.title} className="bg-[hsl(var(--card))] border border-border p-8">
                <p.icon className="w-6 h-6 text-[hsl(var(--primary))] mb-6" strokeWidth={1.25} />
                <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program editorial split */}
      <section id="program" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 overflow-hidden aspect-[5/4]">
          <img src={MEAL_IMG} alt="Rainbow bowl of nourishing food" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-6">
          <div className="label-eyebrow mb-4">The Measurement, Reimagined</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
            We measure the <em className="italic">body</em>,
            not the number on the scale.
          </h2>
          <p className="mt-6 text-stone-600 leading-relaxed max-w-md">
            Waist. Hips. Chest. Arms. Thighs. Calves. We chart your shape over time — because a body
            is a landscape, not a score. Watch yours shift, honestly, without the tyranny of a scale.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-border pt-8">
            <div><div className="font-serif text-4xl">6</div><div className="label-eyebrow mt-2">Body Zones</div></div>
            <div><div className="font-serif text-4xl">1</div><div className="label-eyebrow mt-2">Month at a time</div></div>
            <div><div className="font-serif text-4xl">∞</div><div className="label-eyebrow mt-2">Daily Practices</div></div>
          </div>
        </div>
      </section>

      {/* Months Arc */}
      <section id="weeks" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 border-t border-border">
        <div className="label-eyebrow mb-4">The Arc</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] max-w-3xl">
          A year, told in <em className="italic">monthly movements</em>.
        </h2>
        <div className="mt-16 grid md:grid-cols-4 gap-0 border-t border-border">
          {[
            { n: "Dec", t: "Root", d: "Foundation & Reset — audit your energy, honor the vessel, meet your why." },
            { n: "Jan", t: "Ignite", d: "Sustained Energy — desire over should, joy as medicine, boundaries without guilt." },
            { n: "Feb", t: "Deepen", d: "Metabolic Fire — meet the inner critic, feel the harder emotions, and move them through." },
            { n: "Mar", t: "Rise", d: "Radiant Balance — future-self letters, rituals of rising, celebration, and what's next." },
          ].map((w, idx) => (
            <div key={w.n} className={`p-8 ${idx > 0 ? "md:border-l" : ""} border-border`}>
              <div className="label-eyebrow text-[hsl(var(--primary))]">Month · {w.n}</div>
              <h3 className="font-serif text-3xl mt-4 mb-4">{w.t}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About Satori OM */}
      <section id="about" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 border-t border-border grid md:grid-cols-12 gap-12" data-testid="about-satoriom">
        <div className="md:col-span-4">
          <div className="label-eyebrow mb-4">Who's behind this</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
            A note from <span className="font-script text-[hsl(var(--primary))]">Satori OM</span>.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-[1.65] text-stone-700">
          <p>
            <em className="italic">Satori OM</em> is devoted to one quiet idea: that <strong className="not-italic">we</strong> don't need to be
            fixed — <strong className="not-italic">we</strong> need to be met. Softly. Slowly. In our own rhythm.
          </p>
          <p>
            <span className="font-script text-2xl text-[hsl(var(--primary))]">Energy Awakens</span> is our first offering — daily nourishment that fit your busy lifestyle. NO MORE HAVING TO GUESS WHAT'S FOR DINNER!!!!!
            Honest movement, and one daily coaching session that lands like a letter, not a lecture. Every meal is prep-timed,
            every measurement is your body as landscape, and every prompt is written to be spoken aloud.
          </p>
          <p>
            We built this because the wellness industry sells before-and-afters. We wanted to make an app that celebrates the <em className="italic">in-between</em>.
          </p>
          <p className="pt-4 font-script text-2xl text-[hsl(var(--foreground))]">
            — With warmth, the Satori OM team.
          </p>
          <a href="https://satoriom.com" target="_blank" rel="noreferrer" data-testid="about-satoriom-link" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--primary))] hover:text-[hsl(var(--foreground))] transition-colors border-b border-[hsl(var(--primary))] pb-0.5">
            Meet the Coaches · satoriom.com <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* Testimonial band */}
      <section className="bg-[hsl(var(--foreground))] text-[hsl(var(--background))] py-24 mt-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4 overflow-hidden aspect-[4/5]">
            <img src={JOURNAL_IMG} alt="Journaling" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-8">
            <div className="label-eyebrow text-stone-400 mb-6">A Note</div>
            <p className="font-serif italic text-3xl md:text-5xl leading-[1.15]">
              "This isn't a wellness app. It's a mirror,
              held with care. You will meet the person you have been becoming, and you will let them in."
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-center">
        <h2 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight max-w-3xl mx-auto">
          One month at a time.
          <br />
          <em className="italic text-[hsl(var(--primary))]">One quiet homecoming.</em>
        </h2>
            <Link
              to="/pricing"
              data-testid="landing-cta-pricing"
              className="inline-flex items-center gap-2 mt-12 bg-[hsl(var(--primary))] text-white px-10 py-5 hover:bg-[hsl(var(--foreground))] transition-colors"
            >
              See membership <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-stone-500">
          <span className="font-serif italic">Energy Awakens</span>
          <span>© {new Date().getFullYear()} · A monthly practice. By <a href="https://satoriom.com" target="_blank" rel="noreferrer" className="underline hover:text-[hsl(var(--primary))]" data-testid="footer-satoriom-landing">Satori OM</a>.</span>
        </div>
      </footer>
    </div>
  );
}
